import { Stringish } from '../types/typeish.type';

/**
 * Various functions used for parsing data
 */
export class ParserHlp {
  // #region General

  /**
   * Sanitize key; replace all firebase illegal characters ., #, $, /, [, ]
   * @param {Stringish} key          key to replace
   * @param {string}    spaceReplace string literal to replace spaces, empty string by default
   * @return {string}
   */
  public static sanitizeKey(key: Stringish, spaceReplace = ''): string {
    if (!key) return '';
    return key
      .replace(/\s/g, spaceReplace)
      .replace(/\./g, '(dot)')
      .replace(/#/g, '(hash)')
      .replace(/\$/g, '(dol)')
      .replace(/\//g, '(slh)')
      .replace(/\[/g, '(slbr)')
      .replace(/\]/g, '(srbr)');
  }

  /**
   * De-sanitize key; replace all literals to characters ., #, $, /, [, ] and space
   * @param {Stringish} key          key to replace
   * @param {string}    spaceReplace if space existed and was replaced by some other character
   * @return {string} de-sanitized key
   */
  public static deSanitizeKey(key: Stringish, spaceReplace = ''): string {
    if (!key) return '';
    if (spaceReplace) key = key.replace(new RegExp(spaceReplace, 'g'), ' ');
    return key
      .replace(/\(dot\)/g, '.')
      .replace(/\(hash\)/g, '#')
      .replace(/\(dol\)/g, '$')
      .replace(/\(slh\)/g, '/')
      .replace(/\(slbr\)/g, '[')
      .replace(/\(srbr\)/g, ']');
  }

  /**
   * Sanitize string to use as safe multiplatform name
   * @param {string} name       raw file name without extension
   * @param {string} extension  file extension (with dot)
   * @param {number} length     limit, 0 = no limit, windows has path length limit set to 256 by default, so we take half of this
   *                            as safe default value
   * @param {string} terminator string used at end of shortened name. default - "~"
   * @return {string}
   */
  public static sanitizeFileName(name: string, extension: string, length = 128, terminator = '~'): string {
    // eslint-disable-next-line max-len, no-useless-escape
    let safeName = name.replace(/[\\\/:*?"<>|\[\]]/g, ' '); // ? all these chars are forbidden by Windows, Unix-like system forbids only "/"
    if (length) {
      const limit = length - extension.length;
      if (safeName.length > limit) {
        safeName = safeName.substring(0, limit - terminator.length) + terminator;
      }
    }
    return safeName + extension;
  }

  /**
   * Inserts separator at given indexes
   * @param {Stringish} str
   * @param {string}    separator
   * @param {number[]}  indexes   SHOULD BE SORTED DESCENDING, or consider offset after insert
   * @return {string}
   * @example
   * splitFormat('1234567890', '-', [8,6,3]) -> '123-456-78-90'
   *
   */
  public static splitFormat(str: Stringish, separator: string, indexes: number[]): string {
    if (!str) return '';
    for (const i of indexes) {
      if (str.length > i) str = str.substring(0, i) + separator + str.substring(i);
    }
    return str;
  }

  // #endregion

  // #region Numbers

  /**
   * Parse float, warns and returns 0 if builded parseFloat returns NaN
   * @param {Stringish} data
   * @return {number} number from string, 0 if NaN or nullish
   */
  public static parseFloat(data: Stringish): number {
    if (data == null) {
      console.warn(`Value '${data}' is nullish`);
      return 0.0;
    }
    // ? Replace any commas with dots
    data = data.replace(',', '.');
    const float = parseFloat(data);
    if (isNaN(float)) {
      console.warn(`Value '${data}' is NaN`);
      return 0.0;
    } else {
      return float;
    }
  }

  /**
   * Removes all non digit characters, truncates to given length
   * @param {Stringish} number
   * @param {number}    maxLength
   * @return {string}
   */
  public static sanitizeNumber(number: Stringish, maxLength = 0): string {
    number = number?.replace(/[^0-9]/g, '') ?? '';
    return maxLength ? number.substring(0, maxLength) : number;
  }

  /**
   * Removes all non digit characters, preserves leading plus, truncates to given length
   * @param {Stringish} phone
   * @param {number}    maxLength
   * @return {string}
   */
  public static sanitizePhone(phone: Stringish, maxLength = 0): string {
    const trimmed = phone?.trim() ?? '';
    const prefix = trimmed.startsWith('+') ? '+' : '';
    const number = ParserHlp.sanitizeNumber(trimmed);
    const sanitized = number ? `${prefix}${number}` : '';
    return maxLength ? sanitized.substring(0, maxLength) : sanitized;
  }

  // #endregion

  // #region Time

  /**
   * Parse date from format any format to number
   * @param {string} dateToParse
   * @param {string} dateSplitter date splitter eg. . or -
   * @return {number} milliseconds from 01.01.1970 midnight
   */
  public static parseDate(dateToParse: string, dateSplitter?: string): number {
    const dateHours = dateToParse.split(' ');
    let ret = 0;
    let splitter = '';
    if (dateSplitter) {
      splitter = dateSplitter;
    } else {
      const index = dateHours[0].search(/\D/);
      splitter = dateHours[0][index];
    }
    const date = dateHours[0].split(splitter);

    // TODO why is it commented?
    // if (dateHours.length > 1) {
    //   const year = parseInt(date[0]);
    //   const month = parseInt(date[1]) - 1;
    //   const day = parseInt(date[2]);
    //   const hoursHHMMSS = dateHours[1].split(':');
    //   const hours = parseInt(hoursHHMMSS[0]);
    //   const minutes = hoursHHMMSS.length > 1 ? parseInt(hoursHHMMSS[1]) : 0;
    //   const seconds = hoursHHMMSS.length > 2 ? parseInt(hoursHHMMSS[2]) : 0;
    //   return new Date(year, month, day, hours, minutes, seconds).valueOf();
    // } else {
    if (parseInt(date[0]) <= 31) {
      // * if day-month-year
      const year = parseInt(date[2]);
      const month = parseInt(date[1]);
      const day = parseInt(date[0]);
      // ? checking if month-day-year; i know it's naive, but readings are at the end of the month
      ret = (month > 12 ? new Date(year, day - 1, month) : new Date(year, month - 1, day)).getTime();
    } else {
      const year = parseInt(date[0]);
      const month = parseInt(date[1]) - 1;
      const day = parseInt(date[2]);
      ret = new Date(year, month, day).getTime();
    }

    return ret;
    // }
  }
  // #endregion

  // #region IBAN

  /**
   * Sanitizes and formats iban number, mainly for inputs, for display use parseIbanDisplay
   * @param {Stringish} iban
   * @return {string}
   */
  public static parseIban(iban: Stringish): string {
    if (iban) {
      iban = ParserHlp.sanitizeIban(iban);
      if (iban.length >= 2 && iban[0] >= '0' && iban[0] <= '9' && iban[1] >= '0' && iban[1] <= '9') {
        iban = 'PL' + iban;
      }
      return ParserHlp.formatIban(iban);
    }
    return '';
  }

  /**
   * Sanitizes and formats iban number, adding '?' in place of missing numbers, USE FOR VIEW ONLY!
   * @param {Stringish} iban
   * @return {string}
   */
  public static parseIbanDisplay(iban: Stringish): string {
    if (!iban || iban == 'PL') return ''; // TODO remove when importer fixed and database cleaned
    iban = ParserHlp.sanitizeIban(iban);
    let country = ParserHlp.getIbanCountry(iban);
    if (!country) {
      country = 'PL';
      iban = country + iban;
    }
    iban = iban.padEnd(ParserHlp.ibanLength[country] ?? 0, '?');
    return ParserHlp.formatIban(iban);
  }

  /**
   * Changes letters to uppercase, removes all not alphanumerical characters
   * @param {Stringish} iban
   * @return {string}
   */
  public static sanitizeIban(iban: Stringish): string {
    return iban?.toLocaleUpperCase().replace(/[^0-9A-Z]/g, '') ?? '';
  }

  /**
   * Returns two first letters of iban, or null, if contains digits
   * @param {string} iban
   * @return {string | null}
   */
  public static getIbanCountry(iban: string): string | null {
    const code = iban.substring(0, 2);
    return code.match(/[A-Z]{2}/) ? code : null;
  }

  /**
   * Inserts space after every 4 characters
   * @param {Stringish} iban
   * @return {string}
   */
  public static formatIban(iban: Stringish): string {
    return iban?.match(/.{1,4}/g)?.join(' ') ?? '';
  }

  // prettier-ignore
  public static readonly ibanLength: { [code: string]: number } = {
    AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29, BY: 28, CH: 21, CR: 22, CY: 28, CZ: 24,
    DE: 22, DK: 18, DO: 28, EE: 20, EG: 29, ES: 24, FI: 18, FR: 27, FO: 18, GB: 22, GE: 22, GI: 23, GL: 18, GR: 27, GT: 28,
    HR: 21, HU: 28, IE: 22, IL: 23, IQ: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28, LC: 32, LI: 21, LT: 20, LU: 20,
    LV: 21, LY: 25, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27, MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25,
    QA: 29, RO: 24, RS: 22, SA: 24, SC: 31, SD: 18, SE: 24, SI: 19, SK: 24, SM: 27, ST: 25, SV: 28, TL: 23, TN: 24, TR: 26,
    UA: 29, VA: 22, VG: 24, XK: 20
  };

  // #endregion

  // #region NIP

  /**
   * Sanitizes and formats nip number, mainly for inputs, for display use parseNipDisplay
   * @param {Stringish} nip
   * @return {string}
   */
  public static parseNip(nip: Stringish): string {
    nip = ParserHlp.sanitizeNip(nip);
    return ParserHlp.formatNip(nip);
  }

  /**
   * Sanitizes and formats nip number, adding '?' in place of missing numbers, USE FOR VIEW ONLY!
   * @param {Stringish} nip
   * @return {string}
   */
  public static parseNipDisplay(nip: Stringish): string {
    nip = ParserHlp.sanitizeNip(nip).padEnd(10, '?');
    return ParserHlp.formatNip(nip);
  }

  /**
   * Removes all non-digits, drops all digits past 10th
   * @param {Stringish} nip
   * @return {string}
   */
  public static sanitizeNip(nip: Stringish): string {
    return ParserHlp.sanitizeNumber(nip, 10);
  }

  /**
   * Formats string XXX-XXX-XX-XX
   * @param {Stringish} nip
   * @return {string}
   */
  public static formatNip(nip: Stringish): string {
    return ParserHlp.splitFormat(nip, '-', [8, 6, 3]);
  }

  // #endregion

  // #region REGON

  /**
   * Sanitizes and formats regon number, mainly for inputs, for display use parseRegonDisplay
   * @param {Stringish} regon
   * @return {string}
   */
  public static parseRegon(regon: Stringish): string {
    regon = ParserHlp.sanitizeNumber(regon, 14);
    return ParserHlp.formatRegon(regon);
  }

  /**
   * Sanitizes and formats regon number, adding '?' in place of missing numbers, USE FOR VIEW ONLY!
   * @param {Stringish} regon
   * @return {string}
   */
  public static parseRegonDisplay(regon: Stringish): string {
    regon = ParserHlp.sanitizeNumber(regon);
    const length = ParserHlp.getRegonValidLength(regon);
    regon = regon.substring(0, length).padEnd(length, '?');
    return ParserHlp.formatRegon(regon);
  }

  /**
   * Removes all non-digits, drops all digits past 14th
   * @param {Stringish} regon
   * @return {string}
   */
  public static sanitizeRegon(regon: Stringish): string {
    return ParserHlp.sanitizeNumber(regon, 14);
  }

  /**
   * Returns 14 for string longer than 9; otherwise returns 9
   * @param {Stringish} regon
   * @return {string}
   */
  public static getRegonValidLength(regon: Stringish): number {
    return (regon?.length ?? 0) > 9 ? 14 : 9;
  }

  /**
   * Formats string XX XXXXXX X or XX XXXXXX X XXXX X
   * @param {Stringish} regon
   * @return {string}
   */
  public static formatRegon(regon: Stringish): string {
    return ParserHlp.splitFormat(regon, ' ', [13, 9, 8, 2]);
  }

  // #endregion
}
